import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

export default function EvaluationTopicsCard() {
  const url = process.env.REACT_APP_URL;
  const { user: authUser } = useSelector((x) => x.auth);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configExists, setConfigExists] = useState(false);
  const [topics, setTopics] = useState([{ name: '' }]);

  const isOfficer = useMemo(() => authUser?.user?.acl === 'Officer', [authUser?.user?.acl]);

  const normalizeTopics = useCallback((inputTopics) => {
    const dedup = new Set();
    const next = (Array.isArray(inputTopics) ? inputTopics : [])
      .map((topic) => ({ name: String(topic?.name || '').trim() }))
      .filter((topic) => topic.name.length > 0)
      .filter((topic) => {
        const key = topic.name.toLowerCase();
        if (dedup.has(key)) {
          return false;
        }
        dedup.add(key);
        return true;
      });

    return next.length > 0 ? next : [{ name: '' }];
  }, []);

  const loadTopics = useCallback(async () => {
    if (!isOfficer) return;

    setLoading(true);
    try {
      const response = await fetch(`${url}/meetingsConfig`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authUser?.token || ''}`,
        },
      });

      if (response.status === 404) {
        setConfigExists(false);
        setTopics([{ name: '' }]);
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to load topics (${response.status}).`);
      }

      const config = await response.json();
      setConfigExists(true);
      setTopics(normalizeTopics(config?.evaluationTopics));
    } catch (err) {
      toast.error(err.message || 'Unable to load evaluation topics.', { position: 'bottom-left' });
    } finally {
      setLoading(false);
    }
  }, [authUser?.token, isOfficer, normalizeTopics, url]);

  useEffect(() => {
    loadTopics();
  }, [loadTopics]);

  const handleTopicChange = (index, value) => {
    setTopics((prev) => prev.map((topic, topicIndex) => (
      topicIndex === index ? { ...topic, name: value } : topic
    )));
  };

  const addTopic = () => {
    setTopics((prev) => [...prev, { name: '' }]);
  };

  const removeTopic = (index) => {
    setTopics((prev) => {
      const next = prev.filter((_, topicIndex) => topicIndex !== index);
      return next.length > 0 ? next : [{ name: '' }];
    });
  };

  const saveTopics = async () => {
    const sanitizedTopics = normalizeTopics(topics).filter((topic) => topic.name);

    setSaving(true);
    try {
      const payload = { evaluationTopics: sanitizedTopics };
      const method = configExists ? 'PATCH' : 'PUT';
      const response = await fetch(`${url}/meetingsConfig`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authUser?.token || ''}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.error || `Failed to save topics (${response.status}).`);
      }

      setConfigExists(true);
      setTopics(sanitizedTopics.length > 0 ? sanitizedTopics : [{ name: '' }]);
    } catch (err) {
      toast.error(err.message || 'Failed to save evaluation topics.', { position: 'bottom-left' });
    } finally {
      setSaving(false);
    }
  };

  if (!isOfficer) {
    return null;
  }

  if (loading) {
    return <p className="mb-0">Loading evaluation topics...</p>;
  }

  return (
    <>
      <Row className="mb-2">
        <Col xs={12}>
          <p className="mb-2">
            Manage the topics that appear in the speech Evaluation form.
          </p>
        </Col>
      </Row>

      <Row className="g-2">
        {topics.map((topic, index) => (
          <Col xs={12} key={`evaluation-topic-row-${index}`}>
            <Row className="g-2 align-items-center">
              <Col>
                <Form.Control
                  type="text"
                  value={topic.name}
                  onChange={(e) => handleTopicChange(index, e.target.value)}
                  placeholder={`Topic ${index + 1}`}
                />
              </Col>
              <Col xs="auto">
                <Button
                  variant="outline-danger"
                  onClick={() => removeTopic(index)}
                >
                  Remove
                </Button>
              </Col>
            </Row>
          </Col>
        ))}
      </Row>

      <Row className="mt-3">
        <Col className="d-flex gap-2 flex-wrap">
          <Button variant="outline-primary" onClick={addTopic}>
            <i className="bi bi-plus-circle me-1" />
            Add topic
          </Button>
          <Button variant="success" onClick={saveTopics} disabled={saving}>
            {saving ? 'Saving...' : 'Save topics'}
          </Button>
        </Col>
      </Row>
    </>
  );
}
